// Rich, structured course catalog powering the premium Courses section.
// This file is additive — it does not touch COURSE_CATEGORIES in content.js,
// which is still used by the Header mega-menu and Footer link lists.

// ---------------------------------------------------------------------------
// Domains (filterable categories shown as pills in the Courses section)
// ---------------------------------------------------------------------------
export const COURSE_DOMAINS = [
  // Accounts & Finance is shown first — integrated CA/CMA/CS programs plus
  // the latest trending finance & accounting certifications.
  { key: "accounts-finance", title: "Accounts & Finance", icon: "Wallet", gradient: "from-amber-500 via-orange-500 to-rose-500" },
  // The five original pillars below are preserved exactly as-is — same keys,
  // titles, icons and gradients — so their courses, cards and styling never change.
  { key: "ai", title: "Artificial Intelligence", icon: "Sparkles", gradient: "from-rose-500 via-red-500 to-red-600" },
  { key: "software", title: "Software Development", icon: "Code2", gradient: "from-blue-500 via-blue-600 to-indigo-600" },
  { key: "data", title: "Data Science", icon: "Database", gradient: "from-cyan-500 via-sky-600 to-blue-600" },
  { key: "cloud", title: "Cloud Computing", icon: "Cloud", gradient: "from-indigo-500 via-purple-500 to-purple-600" },
  { key: "security", title: "Cyber Security", icon: "ShieldCheck", gradient: "from-slate-700 via-slate-800 to-blue-900" },
  { key: "banking", title: "Banking", icon: "Landmark", gradient: "from-emerald-600 via-green-600 to-green-700" },
  { key: "analytics", title: "Business Analytics & Intelligence", icon: "BarChart3", gradient: "from-sky-500 via-indigo-500 to-indigo-600" },
  { key: "entrepreneurship", title: "Entrepreneurship", icon: "Rocket", gradient: "from-orange-500 via-amber-500 to-yellow-500" },
  { key: "digital-marketing", title: "Digital Marketing", icon: "Megaphone", gradient: "from-fuchsia-500 via-pink-600 to-rose-600" },
  { key: "ui-ux", title: "UI/UX Design", icon: "Palette", gradient: "from-purple-500 via-fuchsia-500 to-pink-600" },
  { key: "project-management", title: "Project Management", icon: "ClipboardList", gradient: "from-blue-500 via-cyan-600 to-teal-600" },
  { key: "full-stack", title: "Full Stack Development", icon: "Layers", gradient: "from-blue-600 via-indigo-600 to-violet-600" },
  { key: "devops", title: "DevOps", icon: "Infinity", gradient: "from-teal-500 via-cyan-600 to-blue-600" },
  { key: "networking", title: "Networking", icon: "Network", gradient: "from-slate-500 via-blue-600 to-indigo-700" },
  { key: "management", title: "Management", icon: "Briefcase", gradient: "from-indigo-500 via-blue-600 to-blue-700" },
  { key: "communication", title: "Communication", icon: "MessageCircle", gradient: "from-pink-500 via-rose-500 to-red-500" },
  { key: "sales", title: "Sales", icon: "Handshake", gradient: "from-lime-500 via-green-600 to-emerald-600" },
  { key: "business-strategy", title: "Business Strategy", icon: "Target", gradient: "from-violet-500 via-purple-600 to-fuchsia-600" },
  { key: "operations", title: "Operations", icon: "Settings2", gradient: "from-stone-500 via-neutral-600 to-zinc-700" },
  { key: "hr", title: "Human Resources", icon: "UserCog", gradient: "from-teal-500 via-emerald-600 to-green-700" },
  { key: "business-law", title: "Business Law", icon: "Gavel", gradient: "from-slate-600 via-gray-700 to-neutral-800" },
  { key: "ecommerce", title: "E-Commerce", icon: "ShoppingCart", gradient: "from-fuchsia-500 via-pink-600 to-rose-600" },
  { key: "media", title: "Media", icon: "Clapperboard", gradient: "from-indigo-500 via-violet-600 to-purple-700" },
  { key: "real-estate", title: "Real Estate", icon: "Home", gradient: "from-emerald-500 via-teal-600 to-cyan-700" },
  { key: "industry", title: "Industry", icon: "Factory", gradient: "from-amber-600 via-orange-600 to-red-700" },
  { key: "other-business", title: "Other Business", icon: "Briefcase", gradient: "from-slate-500 via-slate-600 to-slate-700" },
];

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/[()+&/]/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const img = (keywords, id) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=900&q=80&sat=-10`;

// ---------------------------------------------------------------------------
// 1. Hand-authored Commerce & Finance program catalog (the 33 requested)
// ---------------------------------------------------------------------------
const commerceFinance = [
  {
    title: "B.Com + CA Integrated",
    domain: "accounts-finance",
    duration: "3 Years",
    level: "Undergraduate",
    badge: "Best Seller",
    image: img("chartered accountant office", "photo-1554224155-6726b3ff858f"),
    description: "A dual-track degree that runs your B.Com alongside CA Foundation, Intermediate and Articleship prep, so you graduate job-ready and exam-ready at once.",
    skills: ["Financial Accounting", "Cost Accounting", "Direct & Indirect Tax", "Auditing Standards", "Corporate Law", "Ind AS", "Excel for Accountants", "Tally & ERP", "Financial Statement Analysis"],
    highlights: ["Dedicated CA Foundation & Inter coaching", "Articleship placement support", "Mock exams every quarter", "Mentorship from practicing CAs"],
    curriculum: [
      { module: "Year 1 — Foundations", topics: ["Financial Accounting Basics", "Business Laws", "Business Mathematics & Statistics", "CA Foundation Paper Prep"] },
      { module: "Year 2 — Core Commerce", topics: ["Corporate Accounting", "Cost & Management Accounting", "Taxation I", "CA Inter Group 1"] },
      { module: "Year 3 — Specialisation", topics: ["Auditing & Assurance", "Advanced Tax", "Financial Management", "CA Inter Group 2 & Articleship Prep"] },
    ],
  },
  {
    title: "MBA + CA",
    domain: "accounts-finance",
    duration: "2 Years",
    level: "Postgraduate",
    badge: "Premium",
    image: img("chartered accountant boardroom", "photo-1454165804606-c3d57bc86b40"),
    description: "A dual credential pairing an MBA with CA Inter/Final prep, built for students who want both a management degree and a professional accounting qualification.",
    skills: ["Corporate Finance", "Financial Reporting (Ind AS)", "Direct & Indirect Tax", "Auditing Standards", "Strategic Financial Management", "Corporate Law", "Cost Management", "Excel & Financial Modelling"],
    highlights: ["Dual credential — MBA + CA", "Dedicated CA Inter/Final coaching alongside MBA", "Mentorship from practicing CAs & CXOs", "Placement track with Big 4, corporates & consulting firms"],
    curriculum: [
      { module: "MBA Core", topics: ["Corporate Finance", "Strategic Management", "Financial Statement Analysis"] },
      { module: "CA Track", topics: ["Advanced Auditing", "Direct & Indirect Tax", "Corporate & Allied Laws"] },
      { module: "Capstone", topics: ["Integrated Finance Project", "CA Final Exam Prep", "Industry Presentation"] },
    ],
  },
  {
    title: "B.Com + CMA",
    domain: "accounts-finance",
    duration: "3 Years",
    level: "Undergraduate",
    badge: "Industry Aligned",
    image: img("cost accounting", "photo-1554224155-8d04cb21cd6c"),
    description: "Combine your B.Com degree with the Cost & Management Accountant (CMA) track to master costing, budgeting and strategic financial management.",
    skills: ["Cost Accounting", "Budgeting & Forecasting", "Managerial Accounting", "Strategic Financial Management", "Operations Costing", "Excel Modelling", "SAP Costing Module", "Variance Analysis"],
    highlights: ["ICMAI-aligned curriculum", "Industry costing case studies", "Placement in manufacturing & FMCG finance teams", "Exam-oriented mentorship"],
    curriculum: [
      { module: "Foundation", topics: ["Fundamentals of Accounting", "Cost Concepts", "Business Economics"] },
      { module: "Intermediate", topics: ["Cost & Management Accounting", "Corporate Laws", "Direct Taxation"] },
      { module: "Final", topics: ["Strategic Cost Management", "Corporate Financial Reporting", "CMA Capstone Project"] },
    ],
  },
  {
    title: "B.Com + CS",
    domain: "accounts-finance",
    duration: "3 Years",
    level: "Undergraduate",
    badge: "Premium",
    image: img("corporate law office", "photo-1521791136064-7986c2920216"),
    description: "Pair your commerce degree with the Company Secretary (CS) qualification to build a career in corporate governance, compliance and secretarial practice.",
    skills: ["Company Law", "Corporate Governance", "Securities Law", "Compliance Management", "Drafting & Documentation", "Board Procedures", "SEBI Regulations", "Business Communication"],
    highlights: ["ICSI-mapped modules", "Moot boardroom simulations", "Internship with law & compliance firms", "1:1 mentorship with practicing CS professionals"],
    curriculum: [
      { module: "CSEET & Foundations", topics: ["Business Communication", "Legal Aptitude", "Economic & Business Environment"] },
      { module: "Executive Programme", topics: ["Company Law", "Setting up of Business", "Tax Laws"] },
      { module: "Professional Programme", topics: ["Governance, Risk & Compliance", "Advanced Tax Laws", "Secretarial Audit"] },
    ],
  },
  // {
  //   title: "B.Com + ACCA",
  //   domain: "certifications",
  //   domainAlt: "accounting",
  //   duration: "3 Years",
  //   level: "Undergraduate",
  //   badge: "Global Certification",
  //   image: img("global finance accounting", "photo-1454165804606-c3d57bc86b40"),
  //   description: "Earn a globally recognised ACCA qualification alongside your B.Com degree — the fast track into international accounting and finance roles.",
  //   skills: ["IFRS", "Financial Reporting", "Performance Management", "Audit & Assurance", "Taxation (UK/Global)", "Corporate & Business Law", "Strategic Business Leadership", "Excel & Data Analysis"],
  //   highlights: ["Gold-approved ACCA learning partner curriculum", "Globally portable qualification", "Fast-track exemptions for B.Com graduates", "Career support for Big 4 & MNC roles"],
  //   curriculum: [
  //     { module: "Applied Knowledge", topics: ["Business & Technology", "Management Accounting", "Financial Accounting"] },
  //     { module: "Applied Skills", topics: ["Corporate & Business Law", "Performance Management", "Taxation", "Financial Reporting", "Audit & Assurance", "Financial Management"] },
  //     { module: "Strategic Professional", topics: ["Strategic Business Leader", "Strategic Business Reporting", "Electives"] },
  //   ],
  // },
  // {
  //   title: "B.Com + CPA",
  //   domain: "certifications",
  //   duration: "3 Years",
  //   level: "Undergraduate",
  //   badge: "Global Certification",
  //   image: img("us cpa accounting", "photo-1450101499163-c8848c66ca85"),
  //   description: "Prepare for the US CPA license while completing your B.Com — ideal for students targeting accounting and audit careers in the US and Middle East.",
  //   skills: ["US GAAP", "Auditing & Attestation", "Business Environment & Concepts", "Financial Accounting & Reporting", "Regulation & US Tax", "Excel Financial Modelling", "Internal Controls", "Business Communication"],
  //   highlights: ["US CPA exam-pattern coaching", "Credit evaluation guidance for 150-hour rule", "Access to CPA review question banks", "Career mapping for Big 4 & MNC audit teams"],
  //   curriculum: [
  //     { module: "Auditing & Attestation (AUD)", topics: ["Ethics & Professional Responsibilities", "Assessing Risk", "Forming Conclusions"] },
  //     { module: "Business Environment (BEC)", topics: ["Corporate Governance", "Economic Concepts", "Financial Management"] },
  //     { module: "Financial Accounting (FAR) & Regulation (REG)", topics: ["US GAAP Reporting", "Federal Taxation", "Business Law"] },
  //   ],
  // },
  // {
  //   title: "B.Com + CFA",
  //   domain: "finance",
  //   duration: "3 Years",
  //   level: "Undergraduate",
  //   badge: "Best Seller",
  //   image: img("cfa investment finance", "photo-1611974789855-9c2a0a7236a3"),
  //   description: "Build a career in investment management by combining your B.Com degree with structured CFA Level I & II preparation.",
  //   skills: ["Investment Analysis", "Portfolio Management", "Equity & Fixed Income", "Financial Statement Analysis", "Quantitative Methods", "Corporate Finance", "Derivatives", "Ethics & Professional Standards"],
  //   highlights: ["CFA-curriculum aligned electives", "Bloomberg terminal lab sessions", "Mock CFA Level I exams", "Placement support with AMCs & broking firms"],
  //   curriculum: [
  //     { module: "CFA Level I Foundations", topics: ["Quantitative Methods", "Economics", "Financial Reporting & Analysis"] },
  //     { module: "CFA Level II Track", topics: ["Equity Valuation", "Fixed Income", "Corporate Issuers"] },
  //     { module: "Applied Investing", topics: ["Portfolio Management", "Derivatives & Alternative Investments", "Capstone Portfolio Project"] },
  //   ],
  // },
  {
    title: "B.Com + Banking & Finance",
    domain: "banking",
    duration: "3 Years",
    level: "Undergraduate",
    badge: "Trending",
    image: img("bank branch finance", "photo-1601597111158-2fceff292cdc"),
    description: "A specialised B.Com track focused on retail and corporate banking operations, credit, risk and treasury for a career in banking straight out of college.",
    skills: ["Retail Banking Operations", "Credit Appraisal", "Risk & Compliance", "Treasury Management", "Core Banking Systems", "KYC & AML", "Loan Processing", "Financial Products"],
    highlights: ["Bank-branch simulation labs", "Guaranteed interview opportunities with partner banks", "RBI-guideline aligned curriculum", "NBFC & fintech internship track"],
    curriculum: [
      { module: "Banking Foundations", topics: ["Principles of Banking", "Core Banking Systems", "KYC/AML Norms"] },
      { module: "Credit & Risk", topics: ["Credit Appraisal", "Risk Management", "NPA Management"] },
      { module: "Modern Banking", topics: ["Digital & Retail Banking", "Treasury Operations", "Bank Financial Statements"] },
    ],
  },
  // {
  //   title: "B.Com + Financial Analytics",
  //   domain: "analytics",
  //   duration: "3 Years",
  //   level: "Undergraduate",
  //   badge: "New",
  //   image: img("financial analytics dashboard", "photo-1551288049-bebda4e38f71"),
  //   description: "Merge your commerce degree with data-driven financial analytics — from Excel modelling to Python and Power BI dashboards for real-world finance decisions.",
  //   skills: ["Excel Financial Modelling", "Power BI", "SQL", "Python for Finance", "Ratio & Trend Analysis", "Forecasting", "Data Visualisation", "Financial Statement Analytics"],
  //   highlights: ["Live dashboard-building projects", "Industry datasets from partner companies", "Power BI & Python certification included", "Analyst-track placement support"],
  //   curriculum: [
  //     { module: "Analytics Foundations", topics: ["Excel for Finance", "SQL Basics", "Statistics for Finance"] },
  //     { module: "Applied Tools", topics: ["Power BI Dashboards", "Python for Financial Data", "Financial Modelling"] },
  //     { module: "Capstone", topics: ["Forecasting Models", "Investment Analytics Project", "Presentation to Industry Panel"] },
  //   ],
  // },
  // {
  //   title: "B.Com + FinTech",
  //   domain: "analytics",
  //   domainAlt: "banking",
  //   duration: "3 Years",
  //   level: "Undergraduate",
  //   badge: "New",
  //   image: img("fintech mobile payments", "photo-1563013544-824ae1b704d3"),
  //   description: "A future-facing B.Com specialisation covering digital payments, blockchain, neobanking and the technology reshaping financial services.",
  //   skills: ["Digital Payments", "Blockchain Basics", "Neobanking Models", "Financial APIs", "RegTech", "UPI & Payment Gateways", "Cybersecurity for Finance", "Product Thinking"],
  //   highlights: ["Guest sessions from fintech founders", "Hands-on payment gateway integration project", "Blockchain fundamentals certification", "Startup & fintech internship track"],
  //   curriculum: [
  //     { module: "FinTech Landscape", topics: ["Digital Payments Ecosystem", "Neobanks & Challenger Banks", "RegTech & Compliance"] },
  //     { module: "Technology Core", topics: ["Blockchain Fundamentals", "APIs in Banking", "Data Security in FinTech"] },
  //     { module: "Applied Project", topics: ["Payment Gateway Integration", "FinTech Product Pitch", "Industry Capstone"] },
  //   ],
  // },
  {
    title: "B.Com (General)",
    domain: "commerce",
    duration: "3 Years",
    level: "Undergraduate",
    badge: "Popular",
    image: img("commerce classroom", "photo-1523240795612-9a054b0db644"),
    description: "The foundational commerce degree covering accounting, economics, taxation and business law — a strong base for further specialisation or immediate employment.",
    skills: ["Financial Accounting", "Business Economics", "Business Law", "Taxation Basics", "Business Statistics", "Business Communication", "Entrepreneurship", "MS Office for Business"],
    highlights: ["Flexible elective baskets", "Semester-wise internships", "Career counselling from Year 1", "Bridge pathway to CA/CMA/CS/MBA"],
    curriculum: [
      { module: "Year 1", topics: ["Financial Accounting", "Business Organisation", "Micro Economics"] },
      { module: "Year 2", topics: ["Corporate Accounting", "Business Law", "Macro Economics"] },
      { module: "Year 3", topics: ["Income Tax Law", "Auditing", "Entrepreneurship Development"] },
    ],
  },
  {
    title: "B.Com (Computers)",
    domain: "commerce",
    duration: "3 Years",
    level: "Undergraduate",
    badge: "Popular",
    image: img("commerce computer lab", "photo-1516321318423-f06f85e504b3"),
    description: "A technology-augmented commerce degree that layers computer applications, tally, spreadsheets and databases onto the core commerce syllabus.",
    skills: ["Financial Accounting", "Computer Applications in Business", "Tally ERP", "Advanced Excel", "Database Fundamentals", "E-Commerce", "Business Law", "Programming Basics"],
    highlights: ["Dedicated computer lab hours", "Industry-recognised Tally certification", "Placement-ready spreadsheet skills", "E-commerce operations module"],
    curriculum: [
      { module: "Year 1", topics: ["Financial Accounting", "Computer Fundamentals", "Business Organisation"] },
      { module: "Year 2", topics: ["Tally ERP & GST", "Database Management", "Corporate Accounting"] },
      { module: "Year 3", topics: ["E-Commerce", "Advanced Excel & Automation", "Auditing"] },
    ],
  },
  {
    title: "B.Com (Honours)",
    domain: "commerce",
    duration: "3 Years",
    level: "Undergraduate",
    badge: "Premium",
    image: img("honours graduation commerce", "photo-1523050854058-8df90110c9f1"),
    description: "An advanced, research-oriented commerce degree with deeper electives in finance, analytics and international business for high-achieving students.",
    skills: ["Advanced Financial Accounting", "Research Methodology", "International Business", "Corporate Finance", "Econometrics", "Advanced Taxation", "Strategic Management", "Business Analytics Basics"],
    highlights: ["Merit-based admission with smaller batches", "Research project & dissertation", "Direct pathway to top MBA programs", "Industry mentorship track"],
    curriculum: [
      { module: "Core Honours", topics: ["Advanced Financial Accounting", "Corporate Finance", "Econometrics"] },
      { module: "Specialised Electives", topics: ["International Business", "Advanced Taxation", "Strategic Management"] },
      { module: "Research Track", topics: ["Research Methodology", "Dissertation", "Industry Presentation"] },
    ],
  },
  {
    title: "Bachelor of Business Administration (BBA)",
    domain: "management",
    duration: "3 Years",
    level: "Undergraduate",
    badge: "Best Seller",
    image: img("business administration students", "photo-1556761175-5973dc0f32e7"),
    description: "A broad-based management degree covering marketing, finance, HR and operations, designed to build well-rounded future business leaders.",
    skills: ["Principles of Management", "Marketing Management", "Financial Management", "HR Management", "Business Communication", "Operations Management", "Business Analytics Basics", "Entrepreneurship"],
    highlights: ["Live business simulation projects", "Industry internship every year", "Leadership & soft-skills lab", "Direct pathway to MBA specialisations"],
    curriculum: [
      { module: "Year 1 — Foundations", topics: ["Principles of Management", "Business Economics", "Business Communication"] },
      { module: "Year 2 — Functional Areas", topics: ["Marketing Management", "Financial Management", "Human Resource Management"] },
      { module: "Year 3 — Strategy", topics: ["Operations Management", "Strategic Management", "Capstone Business Plan"] },
    ],
  },
  {
    title: "BBA + MBA Integrated",
    domain: "management",
    duration: "5 Years",
    level: "Undergraduate + Postgraduate",
    badge: "Premium",
    image: img("mba students discussion", "photo-1552664730-d307ca884978"),
    description: "A seamless five-year pathway from BBA into a chosen MBA specialisation — save time and cost while building a deeper leadership skill set.",
    skills: ["Management Fundamentals", "Strategic Leadership", "Financial Management", "Marketing Strategy", "Operations & Supply Chain", "Business Analytics", "Negotiation", "Corporate Strategy"],
    highlights: ["Guaranteed MBA seat on completing BBA benchmarks", "Two capstone projects across the program", "Dedicated placement cell for both exit points", "Specialisation choice in Year 4"],
    curriculum: [
      { module: "BBA Phase (Yrs 1–3)", topics: ["Management Foundations", "Functional Business Areas", "Business Plan Capstone"] },
      { module: "MBA Core (Yr 4)", topics: ["Strategic Management", "Corporate Finance", "Organisational Behaviour"] },
      { module: "MBA Specialisation (Yr 5)", topics: ["Chosen Specialisation Electives", "Industry Project", "Final Thesis"] },
    ],
  },
  {
    title: "MBA + CS",
    domain: "accounts-finance",
    duration: "2 Years",
    level: "Postgraduate",
    badge: "Premium",
    image: img("boardroom governance meeting", "photo-1573497491208-6b1acb260507"),
    description: "Pair an MBA with the Company Secretary qualification to lead corporate governance, compliance and strategy functions at the same time.",
    skills: ["Corporate Governance", "Strategic Management", "Company Law", "Compliance & Risk", "Mergers & Acquisitions", "Business Ethics", "Board Advisory", "Financial Strategy"],
    highlights: ["Dual credential — MBA + CS", "Boardroom simulation workshops", "M&A case study lab", "Mentorship from company secretaries & CXOs"],
    curriculum: [
      { module: "MBA Core", topics: ["Strategic Management", "Corporate Finance", "Organisational Leadership"] },
      { module: "CS Track", topics: ["Company Law", "Governance, Risk & Compliance", "Secretarial Audit"] },
      { module: "Capstone", topics: ["M&A Case Studies", "Governance Advisory Project", "Board Presentation"] },
    ],
  },
  {
    title: "MBA + Finance",
    domain: "accounts-finance",
    duration: "2 Years",
    level: "Postgraduate",
    badge: "Best Seller",
    image: img("finance mba stock market", "photo-1590283603385-17ffb3a7f29f"),
    description: "A finance-focused MBA covering corporate finance, investment banking, valuation and risk — built for careers in banking, PE and corporate finance teams.",
    skills: ["Corporate Finance", "Investment Banking", "Valuation Modelling", "Risk Management", "Mergers & Acquisitions", "Financial Markets", "Portfolio Strategy", "Excel & Financial Modelling"],
    highlights: ["Live valuation & deal-structuring projects", "Guest lectures from investment bankers", "Bloomberg/Excel modelling labs", "Placement track with banks, PE & consulting firms"],
    curriculum: [
      { module: "Core Finance", topics: ["Corporate Finance", "Financial Markets & Institutions", "Financial Statement Analysis"] },
      { module: "Advanced Finance", topics: ["Investment Banking", "Valuation & M&A", "Risk Management"] },
      { module: "Capstone", topics: ["Deal Simulation Project", "Portfolio Strategy", "Industry Presentation"] },
    ],
  },
  {
    title: "MBA + Business Analytics",
    domain: "analytics",
    duration: "2 Years",
    level: "Postgraduate",
    badge: "Trending",
    image: img("business analytics dashboard team", "photo-1460925895917-afdab827c52f"),
    description: "Blend management strategy with data analytics — SQL, Python, Power BI and predictive modelling — to lead data-driven business decisions.",
    skills: ["SQL", "Python for Analytics", "Power BI & Tableau", "Predictive Modelling", "Business Strategy", "Statistics", "A/B Testing", "Data Storytelling"],
    highlights: ["Live analytics capstone with partner companies", "Power BI & SQL certification included", "Case competitions with real business data", "Analyst & consulting placement track"],
    curriculum: [
      { module: "Analytics Foundations", topics: ["SQL & Databases", "Statistics for Business", "Excel & Power BI"] },
      { module: "Applied Analytics", topics: ["Python for Data Analysis", "Predictive Modelling", "A/B Testing"] },
      { module: "Strategy Capstone", topics: ["Data-Driven Strategy Project", "Dashboard Build", "Industry Presentation"] },
    ],
  },
  // {
  //   title: "MBA + FinTech",
  //   domain: "analytics",
  //   domainAlt: "banking",
  //   duration: "2 Years",
  //   level: "Postgraduate",
  //   badge: "New",
  //   image: img("fintech startup team", "photo-1620714223084-8fcacc6dfd8d"),
  //   description: "An MBA specialisation built around digital finance — payments, lending tech, blockchain and regulatory strategy for the fintech industry.",
  //   skills: ["Digital Lending", "Payments Infrastructure", "Blockchain & Crypto Basics", "RegTech", "Product Strategy", "Risk & Compliance", "Growth Strategy", "Financial Modelling"],
  //   highlights: ["Fintech founder guest sessions", "Product strategy simulation", "Regulatory sandbox case studies", "Startup internship track"],
  //   curriculum: [
  //     { module: "FinTech Strategy", topics: ["Digital Finance Landscape", "Payments & Lending Tech", "RegTech & Compliance"] },
  //     { module: "Product & Growth", topics: ["Product Strategy", "Growth & Distribution", "Risk in Digital Finance"] },
  //     { module: "Capstone", topics: ["FinTech Business Plan", "Investor Pitch Simulation", "Industry Presentation"] },
  //   ],
  // },
  {
    title: "MBA + Marketing",
    domain: "management",
    duration: "2 Years",
    level: "Postgraduate",
    badge: "Best Seller",
    image: img("marketing strategy meeting", "photo-1533750349088-cd871a92f312"),
    description: "Build brand, growth and consumer-insight expertise with an MBA specialised in modern marketing strategy and digital channels.",
    skills: ["Brand Management", "Consumer Behaviour", "Digital Marketing", "Marketing Analytics", "Campaign Strategy", "Product Marketing", "Market Research", "Growth Marketing"],
    highlights: ["Live brand campaign capstone", "Google & Meta certification pathways", "Market research field project", "Placement track with FMCG & D2C brands"],
    curriculum: [
      { module: "Marketing Core", topics: ["Consumer Behaviour", "Market Research", "Brand Management"] },
      { module: "Digital & Growth", topics: ["Digital Marketing Channels", "Marketing Analytics", "Growth Strategy"] },
      { module: "Capstone", topics: ["Integrated Campaign Project", "Go-to-Market Plan", "Client Presentation"] },
    ],
  },
  {
    title: "MBA + Human Resource Management",
    domain: "management",
    duration: "2 Years",
    level: "Postgraduate",
    badge: "Popular",
    image: img("hr team meeting office", "photo-1521737604893-d14cc237f11d"),
    description: "Develop expertise in talent strategy, organisational design and people analytics with an MBA focused on modern HR leadership.",
    skills: ["Talent Acquisition", "Organisational Development", "Compensation & Benefits", "People Analytics", "Labour Law", "Performance Management", "Employee Engagement", "Change Management"],
    highlights: ["HR analytics tool training", "Live talent strategy project with partner companies", "Labour law compliance workshop", "Placement track with HR & consulting teams"],
    curriculum: [
      { module: "HR Foundations", topics: ["Organisational Behaviour", "Talent Acquisition", "Labour Law"] },
      { module: "Strategic HR", topics: ["Compensation & Benefits", "Performance Management", "People Analytics"] },
      { module: "Capstone", topics: ["Change Management Project", "HR Strategy Presentation", "Industry Panel Review"] },
    ],
  },
  {
    title: "MBA + International Business",
    domain: "management",
    duration: "2 Years",
    level: "Postgraduate",
    badge: "Trending",
    image: img("global trade business", "photo-1521790361543-f645cf042ec4"),
    description: "Prepare for cross-border business roles with a curriculum covering global trade, export-import operations and international strategy.",
    skills: ["International Trade Law", "Export-Import Operations", "Global Supply Chain", "Cross-Cultural Management", "Foreign Exchange", "International Marketing", "Trade Finance", "Global Strategy"],
    highlights: ["Trade documentation simulation lab", "Study of global market entry strategies", "Foreign exchange & trade finance module", "Placement track with export houses & MNCs"],
    curriculum: [
      { module: "Global Foundations", topics: ["International Trade Law", "Global Economics", "Cross-Cultural Management"] },
      { module: "Trade Operations", topics: ["Export-Import Documentation", "Trade Finance", "Global Supply Chain"] },
      { module: "Strategy Capstone", topics: ["Market Entry Strategy Project", "International Business Plan", "Industry Presentation"] },
    ],
  },
  {
    title: "MBA + Operations Management",
    domain: "management",
    duration: "2 Years",
    level: "Postgraduate",
    badge: "Popular",
    image: img("operations supply chain warehouse", "photo-1553413077-190dd305871c"),
    description: "Master supply chain, process optimisation and quality management with an MBA built for operations and manufacturing leadership roles.",
    skills: ["Supply Chain Management", "Process Optimisation", "Lean & Six Sigma", "Inventory Management", "Quality Management", "Vendor Management", "Logistics Strategy", "Production Planning"],
    highlights: ["Lean Six Sigma Green Belt pathway", "Factory & warehouse live projects", "Supply chain simulation software training", "Placement track with manufacturing & logistics firms"],
    curriculum: [
      { module: "Operations Foundations", topics: ["Production & Operations Management", "Supply Chain Fundamentals", "Quality Management"] },
      { module: "Process Excellence", topics: ["Lean & Six Sigma", "Inventory & Logistics", "Vendor Management"] },
      { module: "Capstone", topics: ["Process Improvement Project", "Supply Chain Strategy", "Industry Presentation"] },
    ],
  },
  {
    title: "MBA + Digital Marketing",
    domain: "management",
    domainAlt: "analytics",
    duration: "2 Years",
    level: "Postgraduate",
    badge: "Trending",
    image: img("digital marketing analytics laptop", "photo-1460925895917-afdab827c52f"),
    description: "Combine management strategy with hands-on performance marketing — SEO, paid ads, analytics and marketing automation.",
    skills: ["SEO & Content Strategy", "Paid Media (Google/Meta)", "Marketing Automation", "Marketing Analytics", "Social Media Strategy", "Conversion Optimisation", "Email Marketing", "Brand Strategy"],
    highlights: ["Live ad-spend campaign management", "Google Ads & Analytics certification pathway", "Marketing automation tool training", "Placement track with digital & D2C brands"],
    curriculum: [
      { module: "Digital Foundations", topics: ["SEO & Content Strategy", "Social Media Marketing", "Email Marketing"] },
      { module: "Performance Marketing", topics: ["Paid Media Campaigns", "Marketing Analytics", "Conversion Optimisation"] },
      { module: "Capstone", topics: ["Live Campaign Management Project", "Growth Strategy Plan", "Client Presentation"] },
    ],
  },
  {
    title: "Certified Financial Planner (CFP)",
    domain: "accounts-finance",
    duration: "6–12 Months",
    level: "Professional Certification",
    badge: "Global Certification",
    image: img("financial planning advisor", "photo-1579621970563-ebec7560ff3e"),
    description: "Become a globally recognised financial planner — covering retirement, tax, estate and investment planning for individual clients.",
    skills: ["Retirement Planning", "Tax Planning", "Estate Planning", "Investment Planning", "Insurance Planning", "Risk Analysis", "Client Advisory", "Financial Plan Drafting"],
    highlights: ["FPSB-aligned curriculum", "Real client case-study practice", "Exam-focused mock tests", "Career support for wealth advisory roles"],
    curriculum: [
      { module: "Foundations", topics: ["Introduction to Financial Planning", "Risk Analysis & Insurance", "Investment Planning"] },
      { module: "Specialised Planning", topics: ["Retirement Planning", "Tax Planning", "Estate Planning"] },
      { module: "Capstone", topics: ["Comprehensive Financial Plan Project", "Client Advisory Simulation", "Certification Exam Prep"] },
    ],
  },
  {
    title: "Investment Banking Program",
    domain: "banking",
    duration: "6 Months",
    level: "Professional Certification",
    badge: "Premium",
    image: img("investment banking trading floor", "photo-1560472354-b33ff0c44a43"),
    description: "An intensive program covering valuation, M&A, LBOs and pitch-book building — modelled on real investment banking analyst workflows.",
    skills: ["Valuation (DCF, Comps)", "Mergers & Acquisitions", "LBO Modelling", "Pitch Book Creation", "Financial Statement Modelling", "Excel & PowerPoint Mastery", "Deal Structuring", "Capital Markets"],
    highlights: ["Analyst-style deal simulations", "Pitch-book building workshop", "Mentorship from ex-investment bankers", "Placement support with boutique & bulge-bracket-adjacent firms"],
    curriculum: [
      { module: "Valuation Foundations", topics: ["DCF Modelling", "Comparable Company Analysis", "Precedent Transactions"] },
      { module: "Deal Mechanics", topics: ["M&A Process", "LBO Modelling", "Deal Structuring"] },
      { module: "Capstone", topics: ["Full Pitch Book Build", "Mock Client Presentation", "Industry Panel Review"] },
    ],
  },
  {
    title: "Stock Market & Trading",
    domain: "accounts-finance",
    duration: "3 Months",
    level: "Professional Certification",
    badge: "Trending",
    image: img("stock market trading screen", "photo-1611974789855-9c2a0a7236a3"),
    description: "Learn equity, derivatives and technical analysis with live market practice — built for aspiring traders and investors alike.",
    skills: ["Technical Analysis", "Fundamental Analysis", "Equity Trading", "Futures & Options", "Risk Management", "Trading Psychology", "Chart Patterns", "Portfolio Building"],
    highlights: ["Live market trading labs", "Paper-trading simulator practice", "Options strategy workshops", "Mentorship from active traders"],
    curriculum: [
      { module: "Market Foundations", topics: ["Stock Market Basics", "Order Types & Exchanges", "Fundamental Analysis"] },
      { module: "Trading Skills", topics: ["Technical Analysis & Chart Patterns", "Futures & Options Strategies", "Risk & Money Management"] },
      { module: "Applied Trading", topics: ["Live Trading Simulation", "Portfolio Construction", "Trading Psychology"] },
    ],
  },
  {
    title: "Financial Modelling & Valuation",
    domain: "accounts-finance",
    duration: "4 Months",
    level: "Professional Certification",
    badge: "Best Seller",
    image: img("financial modelling excel spreadsheet", "photo-1554224155-6726b3ff858f"),
    description: "Build bankable, three-statement financial models and valuations from scratch in Excel — a core skill for finance and equity research roles.",
    skills: ["Three-Statement Modelling", "DCF Valuation", "Sensitivity Analysis", "Ratio Analysis", "Scenario Modelling", "Advanced Excel", "Equity Research Basics", "Presentation of Models"],
    highlights: ["Build 5+ real company models", "Advanced Excel & shortcuts mastery", "Equity research report writing", "Placement support with research & finance teams"],
    curriculum: [
      { module: "Modelling Foundations", topics: ["Advanced Excel for Finance", "Three-Statement Linking", "Working Capital Modelling"] },
      { module: "Valuation Techniques", topics: ["DCF Valuation", "Comparable Company Analysis", "Sensitivity & Scenario Analysis"] },
      { module: "Capstone", topics: ["End-to-End Company Model Build", "Equity Research Report", "Model Presentation"] },
    ],
  },
  {
    title: "GST & Taxation Professional",
    domain: "accounts-finance",
    duration: "2 Months",
    level: "Professional Certification",
    badge: "Popular",
    image: img("tax documents calculator", "photo-1554224154-22dec7ec8818"),
    description: "Master GST filing, income tax computation and compliance workflows used by accountants and tax consultants every day.",
    skills: ["GST Registration & Filing", "Income Tax Computation", "TDS/TCS", "E-Way Bills", "GST Reconciliation", "Tax Software Tools", "Compliance Calendars", "Audit Documentation"],
    highlights: ["Hands-on GST portal filing practice", "Real business tax-filing case studies", "Updated with latest tax amendments", "Placement support with CA firms & consultancies"],
    curriculum: [
      { module: "GST Fundamentals", topics: ["GST Structure & Registration", "Input Tax Credit", "GST Returns Filing"] },
      { module: "Income Tax", topics: ["Tax Computation for Individuals & Firms", "TDS/TCS Provisions", "Tax Filing Software"] },
      { module: "Applied Compliance", topics: ["GST Reconciliation & Audit", "Compliance Calendar Management", "Case Study Filing Project"] },
    ],
  },
  {
    title: "SAP FICO",
    domain: "accounts-finance",
    duration: "3 Months",
    level: "Professional Certification",
    badge: "Industry Aligned",
    image: img("sap enterprise software office", "photo-1551288049-bebda4e38f71"),
    description: "Get hands-on with SAP's Finance & Controlling module — the enterprise accounting system used by large organisations worldwide.",
    skills: ["SAP FI Configuration", "SAP CO Configuration", "General Ledger", "Accounts Payable/Receivable", "Asset Accounting", "Cost Center Accounting", "Bank Reconciliation in SAP", "Reporting in SAP"],
    highlights: ["Live SAP sandbox access", "End-to-end configuration practice", "Real business-process case studies", "Placement support for ERP finance roles"],
    curriculum: [
      { module: "SAP FI", topics: ["General Ledger Setup", "Accounts Payable & Receivable", "Bank Accounting"] },
      { module: "SAP CO", topics: ["Cost Center Accounting", "Internal Orders", "Profitability Analysis"] },
      { module: "Applied Configuration", topics: ["Asset Accounting", "Month-End Closing Process", "End-to-End Project"] },
    ],
  },
  {
    title: "Tally Prime with GST",
    domain: "accounts-finance",
    duration: "6 Weeks",
    level: "Certificate",
    badge: "Popular",
    image: img("tally accounting software office", "photo-1554224155-8d04cb21cd6c"),
    description: "The most in-demand accounting software skill — master bookkeeping, inventory, payroll and GST filing on Tally Prime.",
    skills: ["Tally Prime Setup", "Voucher Entry", "Inventory Management", "GST Filing in Tally", "Payroll Processing", "Bank Reconciliation", "MIS Reports", "Data Backup & Security"],
    highlights: ["Practice on real business datasets", "GST return filing walkthroughs", "Payroll & inventory hands-on modules", "Certificate recognised by local accounting firms"],
    curriculum: [
      { module: "Tally Basics", topics: ["Company Creation & Setup", "Ledger & Voucher Entry", "Inventory Basics"] },
      { module: "GST & Compliance", topics: ["GST Configuration", "GST Returns in Tally", "TDS in Tally"] },
      { module: "Advanced Tally", topics: ["Payroll Processing", "MIS & MIS Reports", "Data Security & Backup"] },
    ],
  },
  {
    title: "Business Analytics with Power BI",
    domain: "analytics",
    duration: "2 Months",
    level: "Professional Certification",
    badge: "Trending",
    image: img("power bi dashboard analytics", "photo-1551288049-bebda4e38f71"),
    description: "Turn raw business data into interactive dashboards and decision-ready insights using Power BI, DAX and data modelling best practices.",
    skills: ["Power BI Desktop", "DAX Formulas", "Data Modelling", "Power Query", "Interactive Dashboards", "KPI Reporting", "Data Cleaning", "Business Storytelling"],
    highlights: ["Build 5+ real dashboards", "DAX formula deep-dive workshop", "Live business dataset projects", "Certificate + portfolio-ready dashboards"],
    curriculum: [
      { module: "Power BI Foundations", topics: ["Power BI Interface", "Data Import & Power Query", "Basic Visualisations"] },
      { module: "Data Modelling & DAX", topics: ["Data Modelling", "DAX Formulas & Measures", "Relationships & Hierarchies"] },
      { module: "Applied Dashboards", topics: ["Interactive Dashboard Build", "KPI Reporting", "Business Storytelling Presentation"] },
    ],
  },
  {
    title: "Excel for Finance Professionals",
    domain: "finance",
    duration: "6 Weeks",
    level: "Certificate",
    badge: "Popular",
    image: img("excel spreadsheet finance laptop", "photo-1554224155-6726b3ff858f"),
    description: "Go from Excel basics to advanced financial functions, dashboards and modelling shortcuts used daily by finance professionals.",
    skills: ["Advanced Formulas", "Pivot Tables", "Financial Functions", "Dashboarding", "What-If Analysis", "Macros & VBA Basics", "Data Validation", "Chart & Report Design"],
    highlights: ["Real finance-team spreadsheet case studies", "Keyboard-shortcut speed workshop", "Dashboard building project", "Certificate of completion"],
    curriculum: [
      { module: "Excel Essentials", topics: ["Formulas & Functions", "Pivot Tables", "Data Validation"] },
      { module: "Finance Functions", topics: ["Financial Functions (NPV, IRR, PMT)", "What-If Analysis", "Sensitivity Tables"] },
      { module: "Automation & Reporting", topics: ["Dashboard Design", "Macros & VBA Basics", "Reporting Project"] },
    ],
  },
  {
    title: "Corporate Accounting & Auditing",
    domain: "accounting",
    duration: "3 Months",
    level: "Professional Certification",
    badge: "Industry Aligned",
    image: img("corporate audit finance office", "photo-1454165804606-c3d57bc86b40"),
    description: "Master corporate books of accounts, statutory audit procedures and financial statement compliance for mid-to-large organisations.",
    skills: ["Corporate Accounting", "Statutory Audit", "Internal Controls", "Financial Statement Preparation", "Ind AS Compliance", "Audit Documentation", "Risk-Based Auditing", "Reporting Standards"],
    highlights: ["Real audit-file case studies", "Ind AS compliance workshop", "Internal controls simulation", "Placement support with audit & assurance teams"],
    curriculum: [
      { module: "Corporate Accounting", topics: ["Preparation of Financial Statements", "Consolidation Basics", "Ind AS Overview"] },
      { module: "Auditing", topics: ["Statutory Audit Process", "Internal Controls & Risk Assessment", "Audit Documentation"] },
      { module: "Applied Practice", topics: ["Mock Audit File Preparation", "Compliance Reporting", "Case Study Presentation"] },
    ],
  },
];

// ---------------------------------------------------------------------------
// 2. Generated cards for existing tech domains (kept from the original site
//    so the section still covers all six original pillars, restyled to
//    match the new premium card format).
// ---------------------------------------------------------------------------
const techSeed = [
  {
    domain: "software",
    duration: "6 Months",
    level: "Beginner to Advanced",
    badge: "Best Seller",
    imageId: "photo-1547658719-da2b51169166",
    descTemplate: (t) => `Build production-grade applications with ${t} through live projects, code reviews and placement-focused mentorship.`,
    skillsPool: ["Version Control (Git)", "REST APIs", "Data Structures", "Testing & Debugging", "Cloud Deployment", "Agile Workflow", "System Design Basics", "Database Design"],
    items: ["Full Stack Java", "MERN Stack", "MEAN Stack", "React.js", "Angular", "Node.js", "Express.js", "Spring Boot", "Java", "Python", "C Programming", "C++", "SQL", "MongoDB", "Oracle Database"],
  },
  {
    domain: "ai",
    duration: "4 Months",
    level: "Intermediate",
    badge: "Trending",
    imageId: "photo-1677442136019-21780ecad995",
    descTemplate: (t) => `Get hands-on with ${t} to design, fine-tune and deploy real generative AI and ML systems used in industry today.`,
    skillsPool: ["Python for AI", "Model Fine-Tuning", "Vector Databases", "API Integration", "Prompt Design", "AI Ethics", "Evaluation Metrics", "Deployment Pipelines"],
    items: ["AI Fundamentals", "Prompt Engineering", "ChatGPT", "Generative AI", "Machine Learning", "Deep Learning", "LangChain", "LangGraph", "OpenAI APIs", "HuggingFace"],
  },
  {
    domain: "data",
    duration: "5 Months",
    level: "Beginner to Advanced",
    badge: "Popular",
    imageId: "photo-1551288049-bebda4e38f71",
    descTemplate: (t) => `Turn raw datasets into decisions using ${t}, with live business case studies and a portfolio of dashboards.`,
    skillsPool: ["Data Cleaning", "Exploratory Data Analysis", "Statistical Modelling", "Data Visualisation", "SQL Querying", "Reporting", "A/B Testing", "Storytelling with Data"],
    items: ["Python", "NumPy", "Pandas", "Statistics", "SQL", "Tableau", "Power BI", "Data Analytics"],
  },
  {
    domain: "cloud",
    duration: "3 Months",
    level: "Intermediate",
    badge: "Industry Aligned",
    imageId: "photo-1451187580459-43490279c0fa",
    descTemplate: (t) => `Deploy, scale and secure production workloads on ${t} with hands-on labs modelled on real infrastructure.`,
    skillsPool: ["Infrastructure as Code", "CI/CD Pipelines", "Container Orchestration", "Cloud Security", "Networking Basics", "Cost Optimisation", "Monitoring & Logging", "Serverless Architecture"],
    items: ["AWS", "Microsoft Azure", "Google Cloud", "Docker", "Kubernetes", "DevOps"],
  },
  {
    domain: "security",
    duration: "4 Months",
    level: "Intermediate to Advanced",
    badge: "Premium",
    imageId: "photo-1550751827-4bd374c3f58b",
    descTemplate: (t) => `Learn to break in, defend and lead security operations with ${t}, using real-world labs and red-team exercises.`,
    skillsPool: ["Vulnerability Assessment", "Network Defense", "Incident Response", "Security Tools", "Threat Modelling", "Compliance Frameworks", "Log Analysis", "Report Writing"],
    items: ["Ethical Hacking", "Network Security", "Penetration Testing", "SOC Analyst", "Kali Linux"],
  },
];

const generatedTech = techSeed.flatMap((seed) =>
  seed.items.map((title, idx) => ({
    title,
    domain: seed.domain,
    duration: seed.duration,
    level: seed.level,
    badge: idx === 0 ? seed.badge : idx % 4 === 0 ? "New" : "Popular",
    image: img(title, seed.imageId),
    description: seed.descTemplate(title),
    skills: [title, ...seed.skillsPool].slice(0, 9),
    highlights: ["Live mentor-led sessions", "Real-world capstone project", "Placement & interview prep", "Certificate of completion"],
    curriculum: [
      { module: "Foundations", topics: [`Introduction to ${title}`, "Core Concepts", "Hands-on Setup"] },
      { module: "Applied Skills", topics: ["Real-World Practice", "Best Practices & Tooling", "Mini Projects"] },
      { module: "Capstone", topics: ["End-to-End Capstone Project", "Portfolio Building", "Interview Preparation"] },
    ],
  }))
);

// ---------------------------------------------------------------------------
// 2b. Newly requested high-priority categories — Digital Marketing, UI/UX
//    Design, Full Stack Development, DevOps and Networking. Added as an
//    entirely separate seed so techSeed (AI/Software/Data/Cloud/Security)
//    above is never touched.
// ---------------------------------------------------------------------------
const newHighPrioritySeed = [
  {
    domain: "digital-marketing",
    duration: "3 Months",
    level: "Beginner to Advanced",
    badge: "Trending",
    imageId: "photo-1460925895917-afdab827c52f",
    descTemplate: (t) => `Master ${t} with live campaigns, real ad budgets and analytics dashboards used by performance marketing teams.`,
    skillsPool: ["SEO", "Paid Media", "Marketing Automation", "Analytics & Reporting", "Content Strategy", "Conversion Optimisation", "Social Media Growth", "Email Marketing"],
    items: ["SEO & Content Marketing", "Google & Meta Ads", "Social Media Marketing", "Marketing Automation", "Influencer Marketing", "Marketing Analytics"],
  },
  {
    domain: "ui-ux",
    duration: "4 Months",
    level: "Beginner to Advanced",
    badge: "Trending",
    imageId: "photo-1551288049-bebda4e38f71",
    descTemplate: (t) => `Design intuitive, user-tested products with ${t}, from wireframes to polished, portfolio-ready prototypes.`,
    skillsPool: ["User Research", "Wireframing", "Prototyping", "Design Systems", "Usability Testing", "Interaction Design", "Visual Design", "Design Handoff"],
    items: ["UI/UX Design Fundamentals", "Figma", "Design Thinking", "User Research", "Interaction Design", "Design Systems"],
  },
  {
    domain: "full-stack",
    duration: "6 Months",
    level: "Beginner to Advanced",
    badge: "Best Seller",
    imageId: "photo-1547658719-da2b51169166",
    descTemplate: (t) => `Ship complete web applications with ${t}, covering frontend, backend, databases and deployment end to end.`,
    skillsPool: ["Frontend Development", "Backend APIs", "Database Design", "Authentication", "Cloud Deployment", "Version Control (Git)", "Testing", "System Design"],
    items: ["Full Stack Web Development", "MERN Stack Developer", "Frontend Development", "Backend Development", "API Development", "Full Stack JavaScript"],
  },
  {
    domain: "devops",
    duration: "4 Months",
    level: "Intermediate",
    badge: "Trending",
    imageId: "photo-1451187580459-43490279c0fa",
    descTemplate: (t) => `Automate builds, deployments and infrastructure with ${t}, using the same CI/CD pipelines and tooling production teams rely on.`,
    skillsPool: ["CI/CD Pipelines", "Infrastructure as Code", "Container Orchestration", "Monitoring & Logging", "Cloud Automation", "Configuration Management", "Site Reliability", "Version Control"],
    items: ["DevOps Fundamentals", "CI/CD Pipelines", "Docker & Kubernetes", "Terraform", "Jenkins", "Site Reliability Engineering"],
  },
  {
    domain: "networking",
    duration: "4 Months",
    level: "Intermediate",
    badge: "Industry Aligned",
    imageId: "photo-1550751827-4bd374c3f58b",
    descTemplate: (t) => `Build practical ${t} skills used to design, configure and secure enterprise networks.`,
    skillsPool: ["Routing & Switching", "Network Security", "Network Design", "Troubleshooting", "Wireless Networking", "Network Monitoring", "IP Addressing", "Firewalls"],
    items: ["Networking Fundamentals", "CCNA", "Enterprise Network Security", "Wireless Networking", "Network Troubleshooting", "Cloud Networking"],
  },
];

const generatedHighPriority = newHighPrioritySeed.flatMap((seed) =>
  seed.items.map((title, idx) => ({
    title,
    domain: seed.domain,
    duration: seed.duration,
    level: seed.level,
    badge: idx === 0 ? seed.badge : idx % 4 === 0 ? "New" : "Popular",
    image: img(title, seed.imageId),
    description: seed.descTemplate(title),
    skills: [title, ...seed.skillsPool].slice(0, 9),
    highlights: ["Live mentor-led sessions", "Real-world capstone project", "Placement & interview prep", "Certificate of completion"],
    curriculum: [
      { module: "Foundations", topics: [`Introduction to ${title}`, "Core Concepts", "Hands-on Setup"] },
      { module: "Applied Skills", topics: ["Real-World Practice", "Best Practices & Tooling", "Mini Projects"] },
      { module: "Capstone", topics: ["End-to-End Capstone Project", "Portfolio Building", "Interview Preparation"] },
    ],
  }))
);

// ---------------------------------------------------------------------------
// 3. Business, management & professional-skills catalog, generated the same
//    way as techSeed above. Covers Entrepreneurship, Communication, Sales,
//    Business Strategy, Operations, Project Management, Business Law, HR,
//    Industry, E-Commerce, Media and Real Estate, plus extra items layered
//    onto the existing Finance, Accounting, Analytics and Management domains.
// ---------------------------------------------------------------------------
const businessSeed = [
  {
    domain: "entrepreneurship",
    duration: "2 Months",
    level: "Beginner to Advanced",
    badge: "Trending",
    imageId: "photo-1553413077-190dd305871c",
    descTemplate: (t) => `Learn ${t} through founder case studies and hands-on projects designed to get your venture off the ground.`,
    skillsPool: ["Idea Validation", "Business Modelling", "Pitching", "Growth Tactics", "Digital Tools", "Market Research", "Bootstrapping", "Networking"],
    items: ["Entrepreneurship Fundamentals", "Startup Business Modelling", "Online Business", "Freelancing", "Venture Funding & Pitching", "Small Business Management"],
  },
  {
    domain: "finance",
    duration: "2 Months",
    level: "Beginner to Advanced",
    badge: "Popular",
    imageId: "photo-1611974789855-9c2a0a7236a3",
    descTemplate: (t) => `Build practical, job-ready skills in ${t} with instructor-led sessions and real financial datasets.`,
    skillsPool: ["Financial Analysis", "Excel Modelling", "Risk Assessment", "Market Research", "Regulatory Awareness", "Reporting", "Forecasting", "Valuation"],
    items: ["Cryptocurrency & Blockchain", "Economics", "Finance Certification & Exam Preparation", "Investing & Trading", "Money Management Tools"],
  },
  {
    domain: "accounting",
    duration: "2 Months",
    level: "Beginner to Advanced",
    badge: "Industry Aligned",
    imageId: "photo-1554224154-22dec7ec8818",
    descTemplate: (t) => `Master ${t} with structured lessons covering standards, tools and real books-of-accounts practice.`,
    skillsPool: ["Bookkeeping", "Ledger Management", "Statutory Compliance", "Reporting Standards", "Reconciliation", "Audit Readiness", "Tax Filing", "ERP Tools"],
    items: ["Accounting & Bookkeeping", "Compliance", "Taxes", "Other Finance & Accounting"],
  },
  {
    domain: "analytics",
    duration: "3 Months",
    level: "Beginner to Advanced",
    badge: "Best Seller",
    imageId: "photo-1551288049-bebda4e38f71",
    descTemplate: (t) => `Apply ${t} to real business datasets and dashboards, from raw numbers to decision-ready insight.`,
    skillsPool: ["Data Cleaning", "Dashboarding", "Statistical Analysis", "Stakeholder Reporting", "SQL Querying", "Data Storytelling", "Forecasting Models", "KPI Design"],
    items: [
      "Business Analytics Fundamentals", "Business Intelligence (BI)", "Data Visualization",
      "Predictive Analytics", "Dashboard Development", "KPI & Performance Analysis",
    ],
  },
  {
    domain: "banking",
    duration: "2 Months",
    level: "Beginner to Advanced",
    badge: "Trending",
    imageIds: [
      "photo-1601597111158-2fceff292cdc", "photo-1560472354-b33ff0c44a43", "photo-1611974789855-9c2a0a7236a3",
      "photo-1573497491208-6b1acb260507", "photo-1521790361543-f645cf042ec4", "photo-1554224155-6726b3ff858f",
      "photo-1554224155-8d04cb21cd6c", "photo-1521791136064-7986c2920216", "photo-1620714223084-8fcacc6dfd8d",
    ],
    descTemplate: (t) => `Build practical ${t} skills used across retail, corporate and digital banking, with real-world case studies.`,
    skillsPool: ["Core Banking Systems", "KYC & AML", "Credit Appraisal", "Regulatory Compliance", "Customer Onboarding", "Risk Assessment", "Digital Banking Tools", "Financial Products"],
    items: ["Retail Banking", "Digital Banking", "Wealth Management", "Banking Risk Management"],
  },
  {
    domain: "communication",
    duration: "6 Weeks",
    level: "Beginner to Advanced",
    badge: "Popular",
    imageId: "photo-1556761175-5973dc0f32e7",
    descTemplate: (t) => `Sharpen your ${t} through live practice sessions, feedback loops and real workplace scenarios.`,
    skillsPool: ["Active Listening", "Storytelling", "Body Language", "Audience Analysis", "Clarity & Structure", "Persuasion", "Confidence Building", "Written Communication"],
    items: ["Business Communication", "Public Speaking", "Presentation Skills", "Negotiation"],
  },
  {
    domain: "management",
    duration: "2 Months",
    level: "Intermediate",
    badge: "Industry Aligned",
    imageId: "photo-1560472354-b33ff0c44a43",
    descTemplate: (t) => `Develop ${t} capability with practical frameworks, case studies and manager-mentored coaching.`,
    skillsPool: ["Delegation", "Feedback & Coaching", "Prioritisation", "Team Building", "Decision-Making", "Accountability", "Stakeholder Management", "Emotional Intelligence"],
    items: ["Leadership", "Team Management", "Change Management", "Performance Management"],
  },
  {
    domain: "sales",
    duration: "6 Weeks",
    level: "Beginner to Advanced",
    badge: "Trending",
    imageId: "photo-1521737604893-d14cc237f11d",
    descTemplate: (t) => `Learn ${t} with role-plays, live pipelines and closing techniques used by top-performing sales teams.`,
    skillsPool: ["Prospecting", "Objection Handling", "Closing Techniques", "Pipeline Management", "CRM Tools", "Relationship Building", "Negotiation", "Forecasting"],
    items: ["Sales Fundamentals", "B2B Sales", "Sales Negotiation", "CRM Management"],
  },
  {
    domain: "business-strategy",
    duration: "2 Months",
    level: "Intermediate to Advanced",
    badge: "Premium",
    imageId: "photo-1523240795612-9a054b0db644",
    descTemplate: (t) => `Build ${t} skills used by strategy teams to analyse markets, competitors and long-term growth plans.`,
    skillsPool: ["Market Sizing", "Competitive Benchmarking", "Scenario Planning", "Business Modelling", "Financial Projections", "Stakeholder Alignment", "Framework Application", "Presentation Building"],
    items: ["Strategic Management", "Business Planning", "Market Analysis", "Growth Strategy"],
  },
  {
    domain: "operations",
    duration: "2 Months",
    level: "Intermediate",
    badge: "Industry Aligned",
    imageIds: [
      "photo-1553413077-190dd305871c", "photo-1523050854058-8df90110c9f1", "photo-1590283603385-17ffb3a7f29f",
      "photo-1620714223084-8fcacc6dfd8d", "photo-1533750349088-cd871a92f312", "photo-1460925895917-afdab827c52f",
      "photo-1552664730-d307ca884978", "photo-1516321318423-f06f85e504b3",
    ],
    descTemplate: (t) => `Get hands-on with ${t}, learning the processes that keep supply chains and operations running efficiently.`,
    skillsPool: ["Process Mapping", "Vendor Management", "Cost Control", "Quality Assurance", "Demand Planning", "Warehouse Operations", "Continuous Improvement", "Reporting"],
    items: ["Operations Management", "Supply Chain Management", "Lean Six Sigma", "Inventory Management"],
  },
  {
    domain: "project-management",
    duration: "6 Weeks",
    level: "Beginner to Advanced",
    badge: "Best Seller",
    imageId: "photo-1516321318423-f06f85e504b3",
    descTemplate: (t) => `Practise ${t} on real project simulations, from planning and estimation through to delivery and retros.`,
    skillsPool: ["Scope Management", "Sprint Planning", "Stakeholder Communication", "Risk Tracking", "Resource Planning", "Reporting Tools", "Timeline Management", "Retrospectives"],
    items: ["Project Management Fundamentals", "Agile", "Scrum", "PMP Preparation", "Kanban", "Jira"],
  },
  {
    domain: "business-law",
    duration: "6 Weeks",
    level: "Intermediate",
    badge: "New",
    imageId: "photo-1521791136064-7986c2920216",
    descTemplate: (t) => `Understand the essentials of ${t} with case studies drawn from real commercial disputes and filings.`,
    skillsPool: ["Legal Research", "Risk Identification", "Documentation Review", "Regulatory Awareness", "Drafting Basics", "Dispute Resolution", "Ethics", "Compliance Reporting"],
    items: ["Contract Law", "Intellectual Property", "Employment Law", "Business Ethics"],
  },
  {
    domain: "hr",
    duration: "2 Months",
    level: "Beginner to Advanced",
    badge: "Popular",
    imageId: "photo-1521790361543-f645cf042ec4",
    descTemplate: (t) => `Build practical ${t} skills, from sourcing and onboarding through to performance and payroll cycles.`,
    skillsPool: ["Sourcing & Screening", "Onboarding Design", "Payroll Basics", "Engagement Surveys", "Appraisal Frameworks", "Labor Compliance", "HR Software", "People Analytics"],
    items: ["HR Management", "Talent Acquisition", "Payroll Management", "HR Analytics"],
  },
  {
    domain: "industry",
    duration: "6 Weeks",
    level: "Beginner to Intermediate",
    badge: "New",
    imageId: "photo-1590283603385-17ffb3a7f29f",
    descTemplate: (t) => `Get an industry-grounded introduction to ${t}, covering how the sector operates and where the roles are.`,
    skillsPool: ["Industry Terminology", "Regulatory Landscape", "Operations Overview", "Vendor & Supplier Basics", "Sector Trends", "Quality Standards", "Case Studies", "Career Pathways"],
    items: ["Manufacturing", "Healthcare Industry", "Retail Industry", "Telecommunications"],
  },
  {
    domain: "ecommerce",
    duration: "6 Weeks",
    level: "Beginner to Advanced",
    badge: "Trending",
    imageId: "photo-1560472354-b33ff0c44a43",
    descTemplate: (t) => `Learn ${t} by building and running a real online store, from setup through to first sales.`,
    skillsPool: ["Store Setup", "Product Listing", "Order Fulfilment", "Ad Campaigns", "Conversion Optimisation", "Payment Integration", "Customer Retention", "Analytics"],
    items: ["Shopify", "Amazon FBA", "Dropshipping", "E-Commerce Marketing"],
  },
  {
    domain: "media",
    duration: "6 Weeks",
    level: "Beginner to Advanced",
    badge: "Popular",
    imageIds: [
      "photo-1556761175-5973dc0f32e7", "photo-1523240795612-9a054b0db644", "photo-1579621970563-ebec7560ff3e",
      "photo-1551288049-bebda4e38f71", "photo-1450101499163-c8848c66ca85", "photo-1521737604893-d14cc237f11d",
      "photo-1554224154-22dec7ec8818", "photo-1563013544-824ae1b704d3",
    ],
    descTemplate: (t) => `Build a portfolio in ${t} with hands-on projects covering planning, production and publishing.`,
    skillsPool: ["Storyboarding", "Editing Tools", "Audience Growth", "Scriptwriting", "Publishing Platforms", "Brand Voice", "Analytics", "Content Calendars"],
    items: ["Content Creation", "Video Production", "Podcasting", "Social Media Content"],
  },
  {
    domain: "real-estate",
    duration: "2 Months",
    level: "Beginner to Advanced",
    badge: "New",
    imageId: "photo-1523240795612-9a054b0db644",
    descTemplate: (t) => `Understand ${t} through market case studies, deal structuring and valuation exercises.`,
    skillsPool: ["Market Research", "Deal Structuring", "Valuation Methods", "Property Documentation", "Client Management", "Financing Basics", "Negotiation", "Regulatory Compliance"],
    items: ["Real Estate Investing", "Property Management", "Real Estate Marketing", "Property Valuation"],
  },
  {
    domain: "other-business",
    duration: "6 Weeks",
    level: "Beginner to Advanced",
    badge: "Popular",
    imageId: "photo-1579621970563-ebec7560ff3e",
    descTemplate: (t) => `A focused, practical course in ${t} designed to build a workplace-ready skill quickly.`,
    skillsPool: ["Practical Application", "Workplace Tools", "Communication", "Research Methods", "Productivity Systems", "Reporting", "Case Studies", "Best Practices"],
    items: ["Business Writing", "Consulting", "Customer Service", "Office Productivity"],
  },
];

const generatedBusiness = businessSeed.flatMap((seed) =>
  seed.items.map((title, idx) => {
    const pool = seed.imageIds && seed.imageIds.length ? seed.imageIds : [seed.imageId];
    const pickedId = pool[idx % pool.length];
    return {
      title,
      domain: seed.domain,
      duration: seed.duration,
      level: seed.level,
      badge: idx === 0 ? seed.badge : idx % 4 === 0 ? "New" : "Popular",
      image: img(title, pickedId),
      description: seed.descTemplate(title),
      skills: [title, ...seed.skillsPool].slice(0, 9),
      highlights: ["Live mentor-led sessions", "Real-world capstone project", "Placement & interview prep", "Certificate of completion"],
      curriculum: [
        { module: "Foundations", topics: [`Introduction to ${title}`, "Core Concepts", "Hands-on Setup"] },
        { module: "Applied Skills", topics: ["Real-World Practice", "Best Practices & Tooling", "Mini Projects"] },
        { module: "Capstone", topics: ["End-to-End Capstone Project", "Portfolio Building", "Interview Preparation"] },
      ],
    };
  })
);

// ---------------------------------------------------------------------------
// 4. Final export — every course, fully normalised with slug + fee
// ---------------------------------------------------------------------------
export const COURSES = [...commerceFinance, ...generatedTech, ...generatedHighPriority, ...generatedBusiness].map((c) => ({
  ...c,
  slug: slugify(c.title),
  fee: "Contact Admissions",
}));
